import { matchPath } from 'react-router-dom';

import { outOfShellRouteEntries } from './route-table';

/**
 * Paths the Terms-of-Service gate (ADR-0497) must never block.
 *
 * The gate wraps the whole router, which is what makes "no matter what page they
 * are on" true. That reach is correct for the app, and wrong for the handful of
 * PUBLIC pages the dashboard bundle also serves: an unsubscribe link, a shared
 * report, the public scan preview, the end-user OAuth consent screen. Those are
 * reachable by people who may not even have a Syncanix account, and forcing a
 * signed-in visitor to accept our terms before they can unsubscribe from an email
 * would be both hostile and, for the unsubscribe case, arguably unlawful.
 *
 * DERIVED from the route table rather than hand-listed, so a new public
 * out-of-shell route is exempt automatically. Hand-listing is how this file would
 * rot into gating a page nobody meant to gate — and the failure would be silent,
 * because the gate looks identical to a working app until someone follows an
 * email link while signed in.
 *
 * Note what is deliberately NOT exempt: the four authenticated interstitials
 * (`/onboarding/create-workspace`, `/cli-auth`, `/checkout`, `/invite/accept`).
 * Those are app surfaces, and `/cli-auth` is the one that matters most — it is
 * where `syncanix login` mints a CLI session, so gating it is what stops a new
 * long-lived credential being issued to someone who has not accepted.
 */
export const TERMS_EXEMPT_PATH_PATTERNS: readonly string[] = outOfShellRouteEntries
  .filter((entry) => !entry.auth)
  .map((entry) => entry.path);

/**
 * Does this pathname resolve to a public route the gate must leave alone?
 *
 * Uses the router's own `matchPath` so parameterised patterns
 * (`/shared/impact/:token`) match the way the router matches them — a
 * `startsWith` check would both over-match (`/shared/impact-internal`) and
 * under-match anything with a parameter in the middle.
 */
export function isTermsExemptPath(pathname: string): boolean {
  return TERMS_EXEMPT_PATH_PATTERNS.some((pattern) => matchPath(pattern, pathname) !== null);
}
